import { FC, useState } from 'react'
import {
  Button, Datagrid, DateField, FunctionField, TextField,
  useGetList, useList, ListContextProvider, Pagination, useTranslate,
  useRefresh, useNotify, useDelete, useRecordContext,
} from 'react-admin'
import { useNavigate } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import EditIcon from '@mui/icons-material/Edit'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Chip, IconButton, Tooltip } from '@mui/material'
import { EnabledChip } from './Chips'
import { getUrl, postJson } from '../providers/data'
import authToken from '../utils/authToken'
import { OrganizationProxyLinkOut } from '../types/apiRequests'

interface ProxyLinksDatagridProps {
  filter: Record<string, unknown>
  createState: Record<string, unknown>
  showOrganization?: boolean
  showProxy?: boolean
}

const DeleteRowButton: FC = () => {
  const record = useRecordContext<{ id: string }>()
  const refresh = useRefresh()
  const notify = useNotify()
  const translate = useTranslate()
  const [deleteOne] = useDelete()

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (record?.id && window.confirm(translate('delete.confirmTitle'))) {
      void deleteOne('organization_proxy_link', { id: record.id }, {
        mutationMode: 'pessimistic',
        // eslint-disable-next-line camelcase
        onSuccess: () => { notify('ra.notification.deleted', { type: 'info', messageArgs: { smart_count: 1 } }); refresh() },
      })
    }
  }

  return (
    <Tooltip title={translate('ra.action.delete')}>
      <IconButton size="small" onClick={handleDelete} color="error">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

const EditRowButton: FC = () => {
  const record = useRecordContext<{ id: string }>()
  const navigate = useNavigate()
  const translate = useTranslate()

  if (!record?.id) return null

  return (
    <Tooltip title={translate('ra.action.edit')}>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/organization_proxy_link/${String(record.id)}`) }}>
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

export const CertificateActions: FC = () => {
  const record = useRecordContext<{ id: string; has_certificate?: boolean; certificate_expiry?: string; certificate_cn?: string }>()
  const refresh = useRefresh()
  const notify = useNotify()
  const translate = useTranslate()
  const [loading, setLoading] = useState(false)

  if (!record) return null

  const handleRegenerate = (e: React.MouseEvent) => {
    e.stopPropagation()
    setLoading(true)
    void postJson(`${getUrl()}/organization_proxy_link/${record.id}/regenerate_certificate`)
      .then(() => { notify('Certificaat gegenereerd', { type: 'success' }); refresh() })
      .catch(() => notify('Certificaat generatie mislukt', { type: 'error' }))
      .finally(() => setLoading(false))
  }

  const handleDownload = (type: 'certificate' | 'key') => (e: React.MouseEvent) => {
    e.stopPropagation()
    const url = `${getUrl()}/organization_proxy_link/${record.id}/${type}`
    const token = authToken.getToken() || ''
    void fetch(url, {
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        'accept-language': 'nl',
      },
    })
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const filename = resp.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1]
          || `${record.certificate_cn || record.id}.${type === 'certificate' ? 'crt' : 'key'}`
        const blob = await resp.blob()
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
      })
      .catch(() => notify('Download mislukt', { type: 'error' }))
  }

  return (
    <>
      {record.has_certificate ? (
        <>
          <Tooltip title={translate('data.downloadCert')}>
            <IconButton size="small" onClick={handleDownload('certificate')}><DownloadIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={translate('data.downloadKey')}>
            <IconButton size="small" onClick={handleDownload('key')}><DownloadIcon fontSize="small" color="warning" /></IconButton>
          </Tooltip>
          <Chip
            size="small"
            label={record.certificate_expiry ? new Date(record.certificate_expiry).toLocaleDateString() : ''}
            sx={{ fontSize: 10, height: 20 }}
          />
        </>
      ) : null}
      <Tooltip title={record.has_certificate ? translate('data.regenerateCert') : translate('data.generateCert')}>
        <IconButton size="small" onClick={handleRegenerate} disabled={loading} color="primary">
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  )
}

export const ProxyLinksDatagrid: FC<ProxyLinksDatagridProps> = ({ filter, createState, showOrganization = false, showProxy = false }) => {
  const navigate = useNavigate()
  const translate = useTranslate()

  const { data, isLoading } = useGetList<OrganizationProxyLinkOut>(
    'organization_proxy_link',
    { pagination: { page: 1, perPage: 100 }, sort: { field: 'created', order: 'DESC' }, filter },
    { enabled: Object.values(filter).every(Boolean) }
  )

  const listContext = useList({ data: data ?? [], isLoading, perPage: 25 })

  return (
    <>
      <Button
        label="resources.organization_proxy_link.create"
        onClick={() => navigate('/organization_proxy_link/create', { state: { record: createState } })}
        sx={{ mb: 2 }}
      >
        <AddIcon />
      </Button>
      <ListContextProvider value={listContext}>
        <Datagrid
          bulkActionButtons={false}
          rowClick={(id) => { navigate(`/organization_proxy_link/${String(id)}`); return false }}
        >
          {showOrganization && <TextField source="organization_display_name" label="data.organization" />}
          {showProxy && <TextField source="proxy_display_name" label="data.proxy" />}
          <TextField source="target_url" />
          <TextField source="auth_type" />
          <FunctionField label="data.enabled" render={() => <EnabledChip />} />
          <FunctionField label="data.certificate" render={() => <CertificateActions />} />
          <DateField source="created" showTime label="data.createdAt" />
          <EditRowButton />
          <DeleteRowButton />
        </Datagrid>
        <Pagination />
      </ListContextProvider>
    </>
  )
}
